import { projectBuyerTicketPurchase } from '@charitystack/shared/group-tickets/buyer';
import type { EventData, Ticket } from '../partials/drawers/types/EventPaymentTypes';

export interface DashboardInventoryCredit {
  groups: number;
  seats: number;
  individuals: number;
  ownedGroupSeatCounts: Record<string, number>;
  releasedGroupSeatCounts: Record<string, number>;
  fullyReleasedGroupIDs: string[];
  ownedIndividualConfirmationIDs: string[];
  releasedIndividualConfirmationIDs: string[];
  ownedGroupSnapshots: Record<string, ExistingPurchasedGroup>;
}

export type DashboardInventoryCredits = Record<string, DashboardInventoryCredit>;

interface ExistingPurchasedGroup {
  purchasedGroupID?: string;
  ticketID: string;
  ticketName?: string;
  seatCount: number;
  groupSizeMode?: 'FIXED' | 'MINIMUM';
  minimumSeats?: number;
  maximumSeats?: number | null;
  pricingMode?: 'FLAT' | 'PER_SEAT';
  unitPriceMinor?: number;
  subtotalAmountMinor?: number;
  discountAmountMinor?: number;
  totalAmountMinor?: number;
  currency?: string;
  promoCode?: string;
  ticketInventoryReleased?: boolean;
  ticketInventoryReleaseOperationKeys?: string[];
}

interface ExistingEventPurchase {
  ticketContractVersion?: number;
  purchasedGroups?: ExistingPurchasedGroup[];
  tickets?: Array<{
    ticketID?: string;
    type?: string;
    purchasedGroupID?: string;
    attendees?: unknown[];
    confirmationID?: string;
    ticketInventoryReleaseReasons?: unknown[];
    pendingRefundRequestID?: string | null;
  }>;
}

const resolveTicketType = (ticket?: {
  type?: string;
  ticketType?: string;
  seats?: number;
}): 'GROUP' | 'SINGLE' =>
  ticket?.type === 'GROUP' || ticket?.ticketType === 'GROUP' || Number(ticket?.seats || 1) > 1
    ? 'GROUP'
    : 'SINGLE';

export const resolveDashboardTicketContractVersion = (
  event?: EventData | null,
): number | undefined =>
  event?.ticketContractVersion === 2 ||
  event?.tickets?.some((ticket) => ticket.groupSizeMode != null)
    ? 2
    : event?.ticketContractVersion;

export const buildDashboardInventoryCredits = (
  purchase?: ExistingEventPurchase | null,
): DashboardInventoryCredits => {
  if (purchase?.ticketContractVersion !== 2) return {};
  const credits: DashboardInventoryCredits = {};
  const getCredit = (ticketID: string) =>
    (credits[ticketID] ||= {
      groups: 0,
      seats: 0,
      individuals: 0,
      ownedGroupSeatCounts: {},
      releasedGroupSeatCounts: {},
      fullyReleasedGroupIDs: [],
      ownedIndividualConfirmationIDs: [],
      releasedIndividualConfirmationIDs: [],
      ownedGroupSnapshots: {},
    });

  const groupsByID = new Map(
    (purchase.purchasedGroups || [])
      .filter((group) => group.purchasedGroupID)
      .map((group) => [group.purchasedGroupID as string, group]),
  );
  const groupAttendeeCounts = new Map<string, number>();

  (purchase.purchasedGroups || []).forEach((group) => {
    if (!group.ticketID) return;
    const credit = getCredit(group.ticketID);
    if (group.purchasedGroupID) {
      credit.ownedGroupSeatCounts[group.purchasedGroupID] = Number(group.seatCount) || 0;
      credit.ownedGroupSnapshots[group.purchasedGroupID] = group;
    }
    if (group.ticketInventoryReleased === true) {
      if (group.purchasedGroupID) credit.fullyReleasedGroupIDs.push(group.purchasedGroupID);
      return;
    }
    credit.groups += 1;
  });
  (purchase.tickets || []).forEach((ticket) => {
    if (!ticket.ticketID) return;
    const isReleased = (ticket.ticketInventoryReleaseReasons?.length || 0) > 0;
    if (ticket.purchasedGroupID || ticket.type === 'GROUP') {
      if (!ticket.purchasedGroupID) return;
      groupAttendeeCounts.set(
        ticket.purchasedGroupID,
        (groupAttendeeCounts.get(ticket.purchasedGroupID) || 0) + 1,
      );
      const group = groupsByID.get(ticket.purchasedGroupID);
      if (isReleased) {
        const credit = getCredit(ticket.ticketID);
        credit.releasedGroupSeatCounts[ticket.purchasedGroupID] =
          (credit.releasedGroupSeatCounts[ticket.purchasedGroupID] || 0) + 1;
      } else if (group?.ticketInventoryReleased !== true) {
        getCredit(ticket.ticketID).seats += 1;
      }
      return;
    }
    if (ticket.confirmationID) {
      getCredit(ticket.ticketID).ownedIndividualConfirmationIDs.push(ticket.confirmationID);
      if (isReleased) {
        getCredit(ticket.ticketID).releasedIndividualConfirmationIDs.push(ticket.confirmationID);
      }
    }
    if (!isReleased) {
      getCredit(ticket.ticketID).individuals += Array.isArray(ticket.attendees)
        ? ticket.attendees.length
        : 1;
    }
  });
  (purchase.purchasedGroups || []).forEach((group) => {
    if (
      !group.ticketID ||
      !group.purchasedGroupID ||
      group.ticketInventoryReleased === true ||
      groupAttendeeCounts.has(group.purchasedGroupID)
    ) {
      return;
    }
    getCredit(group.ticketID).seats += Number(group.seatCount) || 0;
  });
  return credits;
};

const serializeCustomInputs = (inputs: Ticket['attendees'][number]['customInputs'] = []) =>
  inputs.map(({ value, ...input }) => ({
    ...input,
    answers: [value],
    inputType: 'attendee',
  }));

const isSafeNonnegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const isCanonicalPurchasedGroupID = (value: unknown): value is string =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const calculateDashboardTicketRowSubtotal = (
  ticket: Ticket,
  event: EventData | null,
): number => {
  const definition = event?.tickets?.find((item) => item.id === ticket.ticketID);
  const attendeeCount = ticket.attendees?.length || 0;
  const resolvedPrice = Number(ticket.ticketPrice ?? definition?.price ?? 0);
  const price = Number.isFinite(resolvedPrice) && resolvedPrice >= 0 ? resolvedPrice : 0;
  const resolvedDefinitionPrice = Number(definition?.price ?? 0);
  const definitionPrice =
    Number.isFinite(resolvedDefinitionPrice) && resolvedDefinitionPrice >= 0
      ? resolvedDefinitionPrice
      : 0;
  // Existing v2 purchases carry an immutable purchase-time subtotal. Prefer it
  // even while the Event list is still loading (or cannot hydrate), otherwise
  // the legacy group-unit fallback understates PER_SEAT purchases.
  const purchaseSeatCount = ticket.purchaseSeatCount;
  const unitPriceMinor = ticket.unitPriceMinor;
  const subtotalAmountMinor = ticket.subtotalAmountMinor;
  const hasSafePurchaseSeatCount =
    isSafeNonnegativeInteger(purchaseSeatCount) && purchaseSeatCount > 0;
  const hasSafeUnitPrice = isSafeNonnegativeInteger(unitPriceMinor);
  const hasSafeSubtotal = isSafeNonnegativeInteger(subtotalAmountMinor);
  const expectedSnapshotSubtotal =
    ticket.pricingMode === 'FLAT' && hasSafeUnitPrice
      ? unitPriceMinor
      : ticket.pricingMode === 'PER_SEAT' && hasSafeUnitPrice && hasSafePurchaseSeatCount
        ? unitPriceMinor * purchaseSeatCount
        : NaN;
  const hasCanonicalSnapshotFields =
    ticket.purchasedGroupID !== undefined ||
    purchaseSeatCount !== undefined ||
    subtotalAmountMinor !== undefined;
  const eventAcceptsV2GroupSnapshot =
    event === null ||
    (resolveDashboardTicketContractVersion(event) === 2 &&
      (definition === undefined || resolveTicketType(definition) === 'GROUP'));
  const hasValidCanonicalSnapshot =
    isCanonicalPurchasedGroupID(ticket.purchasedGroupID) &&
    hasSafePurchaseSeatCount &&
    purchaseSeatCount === attendeeCount &&
    hasSafeUnitPrice &&
    hasSafeSubtotal &&
    Number.isSafeInteger(expectedSnapshotSubtotal) &&
    subtotalAmountMinor === expectedSnapshotSubtotal &&
    eventAcceptsV2GroupSnapshot;
  if (hasValidCanonicalSnapshot) {
    return subtotalAmountMinor / 100;
  }
  if (
    resolveDashboardTicketContractVersion(event) === 2 &&
    resolveTicketType(definition) === 'GROUP'
  ) {
    const purchasePrice = hasCanonicalSnapshotFields
      ? definitionPrice
      : hasSafeUnitPrice
        ? unitPriceMinor / 100
        : price;
    const fallbackPricingMode = hasCanonicalSnapshotFields
      ? definition?.pricingMode
      : (ticket.pricingMode ?? definition?.pricingMode);
    return fallbackPricingMode === 'PER_SEAT'
      ? purchasePrice * attendeeCount
      : purchasePrice;
  }
  return price * (attendeeCount / (Number(ticket.seats) || 1));
};

export const buildDashboardEventPurchase = (
  event: EventData | null,
  rows: Ticket[],
  inventoryCredits: DashboardInventoryCredits = {},
) => {
  const ticketContractVersion = resolveDashboardTicketContractVersion(event);
  if (ticketContractVersion === 2) {
    (event?.tickets || []).forEach((definition) => {
      const selectedRows = (rows || []).filter((row) => row.ticketID === definition.id);
      if (selectedRows.length === 0) return;
      const credit = inventoryCredits[definition.id];
      const type = resolveTicketType(definition);
      if (type === 'GROUP') {
        const selectedGroups = selectedRows.reduce(
          (sum, row) =>
            sum + (credit?.fullyReleasedGroupIDs.includes(row.purchasedGroupID || '') ? 0 : 1),
          0,
        );
        const selectedSeats = selectedRows.reduce((sum, row) => {
          const groupID = row.purchasedGroupID || '';
          const requestedSeats = row.attendees?.length || 0;
          const ownedSeats = credit?.ownedGroupSeatCounts[groupID];
          const releasedSeats = Number(credit?.releasedGroupSeatCounts[groupID] || 0);
          if (releasedSeats > 0 && ownedSeats != null && requestedSeats !== ownedSeats) {
            throw new RangeError(
              `${definition.name} cannot be resized after ticket inventory was released`,
            );
          }
          return sum + Math.max(requestedSeats - releasedSeats, 0);
        }, 0);
        const remainingGroups = Math.max(
          0,
          Number(definition.quantity || 0) -
            Math.max(0, Number(definition.sold || 0) - Number(credit?.groups || 0)),
        );
        const remainingSeats = Math.max(
          0,
          Number(definition.seatCapacity || 0) -
            Math.max(0, Number(definition.seatsSold || 0) - Number(credit?.seats || 0)),
        );
        if (selectedGroups > remainingGroups) {
          throw new RangeError(`${definition.name} does not have enough groups remaining`);
        }
        if (selectedSeats > remainingSeats) {
          throw new RangeError(`${definition.name} does not have enough seats remaining`);
        }
      } else {
        const selectedIndividuals = selectedRows.reduce(
          (sum, row) =>
            sum +
            (row.attendees || []).filter(
              (attendee) =>
                !credit?.releasedIndividualConfirmationIDs.includes(attendee.confirmationID || ''),
            ).length,
          0,
        );
        const remainingIndividuals = Math.max(
          0,
          Number(definition.quantity || 0) -
            Math.max(0, Number(definition.sold || 0) - Number(credit?.individuals || 0)),
        );
        if (selectedIndividuals > remainingIndividuals) {
          throw new RangeError(`${definition.name} does not have enough tickets remaining`);
        }
      }
    });
  }
  const buyerTickets = (rows || []).map((row) => {
    const definition = event?.tickets?.find((item) => item.id === row.ticketID);
    const type = resolveTicketType(definition);
    const credit = inventoryCredits[row.ticketID || ''];
    const purchaseUnitPrice = Number.isInteger(row.unitPriceMinor)
      ? Number(row.unitPriceMinor) / 100
      : Number(row.ticketPrice ?? definition?.price ?? 0);
    const projectionDefinition = definition
      ? {
          ...definition,
          price: purchaseUnitPrice,
          pricingMode: row.pricingMode ?? definition.pricingMode,
          sold: Math.max(
            0,
            Number(definition.sold || 0) -
              (type === 'GROUP' ? Number(credit?.groups || 0) : Number(credit?.individuals || 0)),
          ),
          ...(type === 'GROUP'
            ? {
                seatsSold: Math.max(
                  0,
                  Number(definition.seatsSold || 0) - Number(credit?.seats || 0),
                ),
              }
            : {}),
        }
      : definition;
    const isV2Group = ticketContractVersion === 2 && type === 'GROUP';
    const purchasedGroupID = isV2Group ? row.purchasedGroupID : undefined;
    if (isV2Group && !purchasedGroupID) {
      throw new TypeError('A v2 Group Ticket selection requires a purchasedGroupID');
    }
    const ownedSeatCount = purchasedGroupID
      ? credit?.ownedGroupSeatCounts[purchasedGroupID]
      : undefined;
    const unchangedOwnedGroup =
      isV2Group && ownedSeatCount != null && ownedSeatCount === (row.attendees?.length || 0);
    if (unchangedOwnedGroup && projectionDefinition) {
      projectionDefinition.sold = Math.max(0, Number(definition?.sold || 0) - 1);
      projectionDefinition.seatsSold = Math.max(
        0,
        Number(definition?.seatsSold || 0) - ownedSeatCount,
      );
      projectionDefinition.archived = false;
      projectionDefinition.ticketSalesPaused = false;
      delete projectionDefinition.ticketSalesPauseReason;
      delete projectionDefinition.ticketSalesPausedAt;
      delete projectionDefinition.ticketSalesPauseIncidentID;
      if (projectionDefinition.groupSizeMode === 'MINIMUM') {
        projectionDefinition.minimumSeats = Math.min(
          Number(projectionDefinition.minimumSeats),
          ownedSeatCount,
        );
        if (projectionDefinition.maximumSeats != null) {
          projectionDefinition.maximumSeats = Math.max(
            Number(projectionDefinition.maximumSeats),
            ownedSeatCount,
          );
        }
      }
    }
    const attendees = (row.attendees || []).map((attendee, index) => ({
      ticketID: row.ticketID,
      ticketName: row.ticketType,
      price: purchaseUnitPrice,
      type,
      ...(type === 'GROUP'
        ? {
            seats: row.attendees.length,
            ...(isV2Group ? { purchasedGroupID, seatIndex: index + 1 } : {}),
          }
        : ticketContractVersion === 2
        ? {}
        : { seats: 1 }),
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      email: attendee.email,
      checkInStatus: attendee.checkInStatus || false,
      confirmationID: attendee.confirmationID,
      pendingRefundRequestID: attendee.pendingRefundRequestID,
      ticketInventoryReleaseReasons: attendee.ticketInventoryReleaseReasons,
      customInputs: serializeCustomInputs(attendee.customInputs),
    }));

    return {
      ...projectionDefinition,
      id: row.ticketID || '',
      name: row.ticketType,
      type,
      price: purchaseUnitPrice,
      quantity: projectionDefinition?.quantity ?? 0,
      sold: projectionDefinition?.sold ?? 0,
      soldNow: type === 'GROUP' ? 1 : attendees.length,
      soldNowPrice: calculateDashboardTicketRowSubtotal(row, event),
      attendees,
      ...(isV2Group
        ? {
            selectedGroups: [
              {
                purchasedGroupID: purchasedGroupID as string,
                ticketID: row.ticketID || '',
                seatCount: attendees.length,
              },
            ],
          }
        : {}),
    };
  });

  const purchase = projectBuyerTicketPurchase({ ticketContractVersion, tickets: buyerTickets });
  const mapping = new Map<
    string,
    { id: string; name: string; numSold: number; soldTotalPrice: number; quantity: number }
  >();
  purchase.ticketMapping.forEach((item) => {
    const existing = mapping.get(item.id);
    const quantity = purchase.tickets.filter((attendee) => attendee.ticketID === item.id).length;
    if (existing) {
      existing.numSold += item.numSold;
      existing.soldTotalPrice += item.soldTotalPrice;
      existing.quantity = quantity;
    } else {
      mapping.set(item.id, { ...item, quantity });
    }
  });

  const purchasedGroups = purchase.purchasedGroups?.map((group) => {
    const snapshot = inventoryCredits[group.ticketID]?.ownedGroupSnapshots[group.purchasedGroupID];
    if (!snapshot) return group;
    if (!snapshot.pricingMode || !Number.isInteger(snapshot.unitPriceMinor)) {
      return { ...snapshot, ...group };
    }
    const pricingMode = snapshot.pricingMode;
    const unitPriceMinor = Number(snapshot.unitPriceMinor);
    const subtotalAmountMinor =
      group.seatCount === snapshot.seatCount && Number.isInteger(snapshot.subtotalAmountMinor)
        ? Number(snapshot.subtotalAmountMinor)
        : pricingMode === 'PER_SEAT'
        ? unitPriceMinor * group.seatCount
        : unitPriceMinor;
    const discountAmountMinor = Math.min(
      Number(snapshot.discountAmountMinor || 0),
      subtotalAmountMinor,
    );
    return {
      ...snapshot,
      seatCount: group.seatCount,
      subtotalAmountMinor,
      discountAmountMinor,
      totalAmountMinor: subtotalAmountMinor - discountAmountMinor,
    };
  });

  return { ...purchase, purchasedGroups, ticketMapping: [...mapping.values()] };
};
